import React from 'react';
import classNames from 'classnames';

import ParkingImage from '../components/Map/ParkingImage';
import { useStore } from '../stores';
import { observer } from 'mobx-react-lite';
import { Layers } from '../stores/layers';
import { scrollToRef } from '../helpers/utils';
import { parse as parseDuration } from 'tinyduration';
import { useTranslateFunction } from '../helpers/useTranslationHook';

import { Close } from '../components/Information'

export const Parking = observer(() => {
  const { t } = useTranslateFunction('parkinglayer');
  const infolayerTranslations = useTranslateFunction('infolayer').t;

  const { layers, parking } = useStore();

  if (layers.activeLayer !== Layers.parking) return null;

  const pricesRef = React.createRef();
  const cheapest = { value: '', price: Infinity };

  const getDurationString = (duration) => {
    try {
      let durationString = '';
      const parsed = parseDuration(duration);
      for (let [key, value] of Object.entries(parsed)) {
        durationString += `${value} ${t(key)} `;
      }
      return durationString;
    } catch (error) {
      return t(duration.toLowerCase().replace('_', ' '));
    }
  };

  let open = true;
  const hideAction = (event) => {
    event?.stopPropagation();
    if (event?.target === event?.currentTarget) {
      layers.closeLayer();
    }
  };

  const formatTimeString = (time) =>
    [time.slice(0, 2), ':', time.slice(2)].join('');

  const timeTableData = [];
  const openingHours = parking.selectedSpot.times.open.map((times) => {
    return {
      text: `${t(times.days[0])} - ${t(times.days[times.days.length - 1])}`,
      value: `${formatTimeString(times.from)} - ${formatTimeString(times.to)}`,
    };
  });

  if (parking?.selectedSpot?.rate_tables?.rate_table.length) {
    for (const rateTable of parking.selectedSpot.rate_tables.rate_table) {
      const first = t(rateTable.active_times[0].days[0]);
      const last = t(
        rateTable.active_times[0].days[
          rateTable.active_times[0].days.length - 1
        ],
      );
      timeTableData.push({
        text: first === last ? `${first}` : `${first} - ${last}`,
        value: `${formatTimeString(
          rateTable.active_times[0].from,
        )} - ${formatTimeString(rateTable.active_times[0].to)}`,
      });

      if (rateTable.rates[0].price < cheapest.price) {
        cheapest.price = rateTable.rates[0].price;
        cheapest.value = rateTable.rates[0].value;
      }

      for (const rates of rateTable.rates) {
        const additionalHour =
          rates.type === 'DURATION_ADDITIONAL' ? t('each_additional_hour') : '';
        const durationString = [
          additionalHour,
          getDurationString(rates.value),
        ].join(' ');
        const lastTime = timeTableData[timeTableData.length - 1];

        const currentTime = {
          text: t(durationString),
          value: `${
            parking?.selectedSpot?.rate_tables.currency
          } ${Number.parseFloat(rates.price).toFixed(2)}`,
          light: true,
        };

        if (lastTime.text !== t(durationString)) {
          timeTableData.push(currentTime);
        } else {
          if (currentTime.value > lastTime.value) {
            timeTableData[timeTableData.length - 1] = currentTime;
          }
        }
      }

      open = false;
      for (let timeSlot of parking.selectedSpot.rate_tables.rate_table[0]
        .active_times) {
        const now = new Date();

        const days = ['SUN', 'MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT'];
        const today = days[now.getDay()];
        const timeSlotIncludesToday = timeSlot.days.indexOf(today) !== -1;

        const currentTimeString =
          ('0' + now.getHours()).slice(-2) + ('0' + now.getMinutes()).slice(-2);

        const closingToday = timeSlot.to > timeSlot.from;

        if (timeSlotIncludesToday) {
          if (closingToday) {
            if (
              currentTimeString > timeSlot.from &&
              currentTimeString < timeSlot.to
            ) {
              open = true;
            }
          } else if (
            // closing the next day
            !(
              currentTimeString > timeSlot.to &&
              currentTimeString < timeSlot.from
            )
          ) {
            open = true;
          }
        }
      }
    }
  }

  return (
    
    <div
      onClick={hideAction}
      className="w-full h-screen pt-[calc(100vH_-_403px)] pb-[150px] overflow-auto absolute top-0 right-0 z-20 lg:w-[400px] shadow-lg lg:bg-white lg:py-0 lg:h-auto lg:top-5 lg:right-5 lg:bottom-5 lg:rounded-xl lg:shadow-xl"
    >
      <div
        onClick={(event) => (event.bubbles = false)}
        className="bg-white w-full p-6 relative shadow-[0_0_14px_0_rgba(0,0,0,0.3)] rounded-tr-[30px] lg:rounded-none lg:shadow-none"
      >
        <Close action={hideAction} />

        <div className="flex items-start relative">
          <ParkingImage
            price={cheapest.price}
            duration={getDurationString(cheapest.value)}
          />

          <div className="pl-5">
            <div className="pl-6 relative text-neutral-400 text-sm">
              <span className="text-[16px] absolute top-[1px] left-0 gys gys-parking"></span>
              {t('parking')}
            </div>

            <div className="my-1 text-xl font-bold text-neutral-900">
              {parking?.selectedSpot?.name}
            </div>

            <div className="mb-2 text-sm text-neutral-600">
              {parking?.selectedSpot?.address?.city}
            </div>

            <div className="flex justify-between">
              <div
                onClick={() => scrollToRef(pricesRef)}
                className={classNames('mr-5 text-sm font-semibold', {
                  'text-success': open,
                  'text-red-700': !open,
                })}
              >
                {open ? t('open') : t('closed')}
                {open && (
                  <span className="ml-2 underline font-normal text-neutral-400">
                    {t('opening_hours_and_prices')}
                  </span>
                )}
              </div>
            </div>
          </div>
        </div>

        <div className="my-6 flex flex-1 justify-between">
          <button
            className="w-full mr-3 border border-neutral-200 py-2 px-3 relative overflow-hidden cursor-pointer text-center text-neutral-600 text-sm rounded-xl transition-all hover:bg-brand-1 hover:text-white"
            onClick={() =>
              window.open(
                `https://www.google.com/maps/dir/?api=1&origin=Current+Location&destination=${parking.currentPosition.coordinates[1]},${parking.currentPosition.coordinates[0]}`,
                '_blank',
              )
            }
          >
            <span className="block text-lg icon icon-navigation"></span>
            {infolayerTranslations('start_navigation')}
          </button>

          <button
            className="w-full border border-neutral-200 py-2 px-3 relative overflow-hidden cursor-pointer text-center text-neutral-600 text-sm rounded-xl transition-all hover:bg-parking hover:text-white"
            onClick={() => {
              parking.parkopedia.setStoreEmpty();
              layers.closeLayer();
            }}
          >
            <span className="block text-lg icon icon-parking"></span>
            {t('hide_parkings')}
          </button>
        </div>

        {parking?.selectedSpot?.address ? (
          <div className="relative mb-6 pl-8">
            <span className="icon icon-region absolute text-xl top-[-2px] left-0 text-neutral-400" />
            {parking?.selectedSpot?.address?.street?.formatted}
            {parking?.selectedSpot?.address?.city}
          </div>
        ) : null}
        {parking?.selectedSpot?.phone ? (
          <div className="relative mb-6 pl-8">
            <span className="icon icon-telephone absolute text-xl top-[-2px] left-0 text-neutral-400" />
            <a href={'tel:' + parking?.selectedSpot?.phone}>
              {parking?.selectedSpot?.phone}
            </a>
          </div>
        ) : null}
        {openingHours?.length ? (
          <div className="relative mb-6 pl-8" ref={pricesRef}>
            <span className="icon icon-clock absolute text-xl top-[-2px] left-0 text-neutral-400" />
            <div className="text-neutral-900 font-bold">
              {t('opening_hours')}
            </div>
            <PriceTable prices={openingHours} />
          </div>
        ) : null}
        <div className="relative mb-6 pl-8">
          <span className="icon icon-tickets absolute text-xl top-[-2px] left-0 text-neutral-400" />
          <div className="text-neutral-900 font-bold">{t('prices')}</div>
          <PriceTable prices={timeTableData} />
        </div>
      </div>
    </div>
  );
});

const PriceTable = ({ prices }) => (
  <table className="table-auto w-full mt-2">
    <tbody>
      {prices?.map((item, index) => (
        <tr key={index}>
          {item.light ? (
            <td>
              <span>{item.text}</span>
            </td>
          ) : (
            <td>{item.text}</td>
          )}
          <td>{item.value}</td>
        </tr>
      ))}
    </tbody>
  </table>
);
